import React, { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField
} from "@mui/material";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { getPobScoreByDateRange, getPobScore } from "../../api/admin.api";

// Columns for display
const requiredColumns = [
  "TeamName",
  "HeadQrt",
  "DoctName",
  "EmpNo",
  "EmpName",
  "Product",
  "STATUS",
  "SUBDATE"
];

const DailyWisePob = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Data
  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [filtered, setFiltered] = useState([]);

  // Filters
  const [teamFilter, setTeamFilter] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  // Mode
  const [mode, setMode] = useState("week");

  // Dates
  const today = new Date().toISOString().split("T")[0];
  const [FromDate, setFromDate] = useState('');
  const [ToDate, setToDate] = useState(today);

  // Format date dd-mm-yyyy
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Process API data
  const processData = (rows) => {
    const formatted = rows.map(row => ({
      ...row,
      SUBDATE: formatDate(row.SUBDATE)
    }));

    setRawData(formatted);
    setData(formatted);
    setFiltered(formatted);
    setPage(1);
  };

  // Fetch WEEK data
  const fetchWeekData = async () => {
    try {
      const response = await getPobScore();
      if (response?.data?.data) {
        processData(response.data.data);
      }
    } catch (error) {
      console.error("WEEK API ERROR:", error);
    }
  };

  // Fetch DATE RANGE data
  const fetchDateRangeData = async () => {
    if (!FromDate || !ToDate) {
      alert("Select both start & end dates");
      return;
    }

    try {
      const response = await getPobScoreByDateRange(FromDate, ToDate);

      console.log("DATE RANGE API RESPONSE:", response);
      if (response?.data?.data) {
        processData(response.data.data);
      }
    } catch (error) {
      console.error("DATE RANGE API ERROR:", error);
    }
  };

  // Handle mode change
  useEffect(() => {
    setTeamFilter("");
    setEmployeeFilter("");
    setProductFilter("");

    if (mode === "week") {
      fetchWeekData();
    }
  }, [mode]);

  // Apply filters
  useEffect(() => {
    let result = [...data];
    if (teamFilter) result = result.filter(r => r.TeamName === teamFilter);
    if (employeeFilter) result = result.filter(r => r.EmpNo === employeeFilter);
    if (productFilter) result = result.filter(r => r.Product === productFilter);
    setFiltered(result);
    setPage(1);
  }, [teamFilter, employeeFilter, productFilter, data]);

  // Paginated data
  const paginatedData = filtered.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Export Excel
  const exportToExcel = () => {
    let excelRows = [...rawData];

    if (teamFilter) excelRows = excelRows.filter(r => r.TeamName === teamFilter);
    if (employeeFilter) excelRows = excelRows.filter(r => r.EmpNo === employeeFilter);
    if (productFilter) excelRows = excelRows.filter(r => r.Product === productFilter);

    const ws = XLSX.utils.json_to_sheet(excelRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "POB_Raw_Data");

    const excelBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array"
    });

    saveAs(new Blob([excelBuffer]), "DailyWisePOB_RawData.xlsx");
  };

  return (
    <Box p={2}>
      <Button variant="outlined" sx={{ mb: 2 }} onClick={() => navigate("/Admin")}>
        ← Back to Admin
      </Button>

      <Typography variant="h4" sx={{ mb: 3 }}>
        Daily Wise POB
      </Typography>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>

        {/* Team Filter */}
        <FormControl sx={{ minWidth: 180 }}>
          <Select value={teamFilter} displayEmpty onChange={(e) => setTeamFilter(e.target.value)}>
            <MenuItem value="">All Teams</MenuItem>
            {[...new Set(data.map(d => d.TeamName).filter(Boolean))].sort().map(team => (
              <MenuItem key={team} value={team}>{team}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Employee Filter */}
        <FormControl sx={{ minWidth: 180 }}>
          <Select value={employeeFilter} displayEmpty onChange={(e) => setEmployeeFilter(e.target.value)}>
            <MenuItem value="">All Employees</MenuItem>
            {[...new Set(data.map(d => d.EmpNo).filter(Boolean))].sort().map(emp => (
              <MenuItem key={emp} value={emp}>{emp}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Product Filter */}
        <FormControl sx={{ minWidth: 180 }}>
          <Select value={productFilter} displayEmpty onChange={(e) => setProductFilter(e.target.value)}>
            <MenuItem value="">All Products</MenuItem>
            {[...new Set(data.map(d => d.Product).filter(Boolean))].sort().map(product => (
              <MenuItem key={product} value={product}>{product}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Mode Toggle */}
        <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
          <FormControlLabel value="week" control={<Radio />} label="Week" />
          <FormControlLabel value="dateRange" control={<Radio />} label="Date Range" />
        </RadioGroup>

        {/* Date Range Inputs */}
        {mode === "dateRange" && (
          <>
            <TextField
              label="Start Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={FromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
            <TextField
              label="End Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={ToDate}
              onChange={(e) => setToDate(e.target.value)}
            />
            <Button variant="contained" onClick={fetchDateRangeData}>
              Submit
            </Button>
          </>
        )}

        {/* Export Button */}
        <Box sx={{ ml: "auto" }}>
          <Button variant="contained" color="success" onClick={exportToExcel}>
            Export Raw Excel
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {requiredColumns.map(col => (
                <TableCell key={col}><strong>{col}</strong></TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, i) => (
              <TableRow key={i}>
                {requiredColumns.map(col => (
                  <TableCell key={col}>{row[col]}</TableCell>
                ))}
              </TableRow>
            ))}

            {paginatedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={requiredColumns.length} align="center">
                  No data found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Pagination
          count={Math.ceil(filtered.length / rowsPerPage)}
          page={page}
          onChange={(e, val) => setPage(val)}
        />
      </Box>
    </Box>
  );
};

export default DailyWisePob;